// Bulk certificate import — up to 4 files at once. Each file is uploaded and
// AI-extracted in parallel; the user reviews/edits each card, then Saves all.
import { useEffect, useRef, useState } from "react";
import { X, Loader2, CheckCircle2, AlertTriangle, FileText, Sparkles } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const ACCENT = "var(--brand-accent, #C75A1B)";
export const BULK_LIMIT = 4;

const inputCls = "w-full bg-white border rounded-[7px] px-2.5 py-1.5 text-[12px] text-[#1F1F1F] outline-none focus:border-[var(--brand-accent,#C75A1B)]";

export default function CertBulkImport({ files, clientId, onClose, onSaved }) {
    const [rows, setRows] = useState([]);
    const [saving, setSaving] = useState(false);
    const started = useRef(false);

    useEffect(() => {
        if (started.current) return;
        started.current = true;
        const initial = files.slice(0, BULK_LIMIT).map((f, i) => ({
            key: `${i}-${f.name}`, fileObj: f, name: f.name,
            status: "scanning", error: null, file: null, confidence: null,
            draft: { category: "accreditation", title: "", issuing_body: "", certificate_number: "", issued_date: "", expiry_date: "", has_expiry: true, scope: "", ai_extracted: true },
        }));
        setRows(initial);
        initial.forEach((row, idx) => extractOne(row, idx));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function extractOne(row, idx) {
        const fd = new FormData();
        fd.append("file", row.fileObj);
        fd.append("client_id", clientId);
        try {
            const { data } = await api.post("/certificates/ai-extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const d = data.draft || {};
            setRows((rs) => rs.map((r, i) => i !== idx ? r : {
                ...r, status: "ready", file: data.file, confidence: d.confidence ?? null,
                draft: {
                    category: d.category || "accreditation", title: d.title || "",
                    issuing_body: d.issuing_body || "", certificate_number: d.certificate_number || "",
                    issued_date: d.issued_date || "", expiry_date: d.expiry_date || "",
                    has_expiry: d.has_expiry !== false && !!d.expiry_date, scope: d.scope || "", ai_extracted: true,
                },
            }));
        } catch (e) {
            setRows((rs) => rs.map((r, i) => i !== idx ? r : { ...r, status: "error", error: formatApiError(e?.response?.data?.detail) || "Extraction failed" }));
        }
    }

    function patchDraft(idx, k, v) {
        setRows((rs) => rs.map((r, i) => i !== idx ? r : { ...r, draft: { ...r.draft, [k]: v } }));
    }
    function removeRow(idx) {
        setRows((rs) => rs.filter((_, i) => i !== idx));
    }

    const scanning = rows.some((r) => r.status === "scanning");
    const savable = rows.filter((r) => r.status === "ready" && r.draft.title.trim());

    async function saveAll() {
        if (!savable.length) { toast.error("Nothing to save — add a title to each certificate."); return; }
        setSaving(true);
        const results = await Promise.allSettled(savable.map((r) => {
            const d = r.draft;
            return api.post("/certificates", {
                client_id: clientId, category: d.category, title: d.title.trim(),
                issuing_body: d.issuing_body || null, certificate_number: d.certificate_number || null,
                issued_date: d.issued_date || null, expiry_date: d.has_expiry ? (d.expiry_date || null) : null,
                has_expiry: !!d.has_expiry, scope: d.scope || null, file: r.file || null, ai_extracted: true,
            });
        }));
        setSaving(false);
        const ok = results.filter((x) => x.status === "fulfilled").length;
        const failed = results.length - ok;
        if (ok) toast.success(`${ok} certificate${ok > 1 ? "s" : ""} saved`);
        if (failed) toast.error(`${failed} failed to save`);
        onSaved();
    }

    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
            <div data-testid="cert-bulk-dialog" onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-[14px] shadow-brand w-full max-w-[820px] max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between px-5 py-3.5 border-b sticky top-0 bg-white z-10" style={{ borderColor: "#F0EDE8" }}>
                    <div className="font-semibold text-[#1F1F1F] flex items-center gap-2">
                        <Sparkles className="w-4 h-4" style={{ color: ACCENT }} /> Bulk import · review {rows.length} certificate{rows.length !== 1 ? "s" : ""}
                        {scanning && <span className="text-[11px] font-normal" style={{ color: "#6F6A64" }}>· reading…</span>}
                    </div>
                    <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-[7px] hover:bg-[#F6F4F0]"><X className="w-4 h-4" /></button>
                </div>

                <div className="p-4 space-y-3">
                    <p className="text-[12px]" style={{ color: "#6F6A64" }}>
                        Each file was scanned by AI — please review and correct before saving. Up to {BULK_LIMIT} at a time.
                    </p>
                    {rows.map((r, idx) => (
                        <div key={r.key} data-testid={`cert-bulk-row-${idx}`} className="border rounded-[10px] p-3" style={{ borderColor: "#E5E0DA", background: "#FCFBF9" }}>
                            <div className="flex items-center gap-2 mb-2">
                                <FileText className="w-4 h-4 shrink-0" style={{ color: "#6F6A64" }} />
                                <span className="text-[12px] font-medium text-[#1F1F1F] truncate flex-1">{r.name}</span>
                                {r.status === "scanning" && <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: "#6F6A64" }}><Loader2 className="w-3.5 h-3.5 animate-spin" /> Reading</span>}
                                {r.status === "ready" && (
                                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: "#3F7D3A" }}>
                                        <CheckCircle2 className="w-3.5 h-3.5" /> Ready{r.confidence != null ? ` · AI ${Math.round(r.confidence * 100)}%` : ""}
                                    </span>
                                )}
                                {r.status === "error" && <span className="inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: "#B33A2E" }}><AlertTriangle className="w-3.5 h-3.5" /> {r.error}</span>}
                                <button onClick={() => removeRow(idx)} className="w-6 h-6 grid place-items-center rounded-[6px] hover:bg-[#FBE8E5]" title="Remove"><X className="w-3.5 h-3.5" style={{ color: "#B33A2E" }} /></button>
                            </div>
                            {r.status === "ready" && (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    <div className="col-span-2">
                                        <input data-testid={`cert-bulk-title-${idx}`} className={inputCls} placeholder="Title / name *" value={r.draft.title} onChange={(e) => patchDraft(idx, "title", e.target.value)} />
                                    </div>
                                    <select className={inputCls} value={r.draft.category} onChange={(e) => patchDraft(idx, "category", e.target.value)}>
                                        <option value="accreditation">Accreditation</option>
                                        <option value="company">Company</option>
                                    </select>
                                    <input className={inputCls} placeholder="Issuing body" value={r.draft.issuing_body} onChange={(e) => patchDraft(idx, "issuing_body", e.target.value)} />
                                    <input className={inputCls} placeholder="Number" value={r.draft.certificate_number} onChange={(e) => patchDraft(idx, "certificate_number", e.target.value)} />
                                    <div>
                                        <span className="text-[9px] uppercase tracking-wide block mb-0.5" style={{ color: "#9A968F" }}>Issued</span>
                                        <input type="date" className={inputCls} value={r.draft.issued_date} onChange={(e) => patchDraft(idx, "issued_date", e.target.value)} />
                                    </div>
                                    <div>
                                        <span className="text-[9px] uppercase tracking-wide block mb-0.5" style={{ color: "#9A968F" }}>Expires</span>
                                        <input type="date" disabled={!r.draft.has_expiry} className={inputCls + (r.draft.has_expiry ? "" : " opacity-50")} value={r.draft.expiry_date} onChange={(e) => patchDraft(idx, "expiry_date", e.target.value)} />
                                    </div>
                                    <label className="flex items-center gap-1.5 text-[11px] text-[#2D2D2D] col-span-2 md:col-span-1 self-end pb-1.5">
                                        <input type="checkbox" checked={!r.draft.has_expiry} onChange={(e) => patchDraft(idx, "has_expiry", !e.target.checked)} /> No expiry
                                    </label>
                                </div>
                            )}
                        </div>
                    ))}
                    {rows.length === 0 && <div className="py-8 text-center italic text-[12px]" style={{ color: "#9A968F" }}>No files to import.</div>}
                </div>

                <div className="flex items-center justify-between gap-2 px-5 py-3.5 border-t sticky bottom-0 bg-white" style={{ borderColor: "#F0EDE8" }}>
                    <span className="text-[11.5px]" style={{ color: "#6F6A64" }}>{savable.length} ready to save</span>
                    <div className="flex items-center gap-2">
                        <button onClick={onClose} className="px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold border hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                        <button data-testid="cert-bulk-save-button" onClick={saveAll} disabled={saving || scanning || !savable.length}
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white disabled:opacity-60" style={{ background: ACCENT }}>
                            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save all ({savable.length})
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
